import { cn } from '@/lib/utils';

/* ---------------------------------------------------------------------------
   Terminal glyphs for the Run Ledger.

   The glyph is the ONE thing an operator reads before anything else on the
   rail, so each terminal state gets a distinct SHAPE and not merely a distinct
   colour: a filled disc, a cross, a dashed ring, a bar. Shape survives a
   colour-blind operator, a bad monitor, and a screenshot pasted into a ticket.

   Geometry is fixed at 11×11 with the centre on the rail. It is identical in
   all three dashboards — that is what makes them read as one product.
--------------------------------------------------------------------------- */

export type GlyphKind =
  | 'pending'
  | 'running'
  | 'succeeded'
  | 'failed'
  | 'expired'
  | 'cancelled'
  | 'skipped';

const TONE: Record<GlyphKind, string> = {
  pending: 'text-ink-3',
  running: 'text-live',
  succeeded: 'text-ok',
  failed: 'text-danger',
  expired: 'text-danger',
  cancelled: 'text-ink-3',
  skipped: 'text-ink-3',
};

export function LedgerGlyph({ kind, className }: { kind: GlyphKind; className?: string }) {
  return (
    <svg
      viewBox="0 0 11 11"
      className={cn('h-[11px] w-[11px] shrink-0', TONE[kind], className)}
      aria-hidden
      focusable="false"
    >
      {/* The ground disc keeps the rail from showing through the glyph. */}
      <circle cx="5.5" cy="5.5" r="5.5" className="fill-[var(--c-surface)]" />

      {kind === 'succeeded' && <circle cx="5.5" cy="5.5" r="3.4" fill="currentColor" />}

      {kind === 'pending' && (
        <circle cx="5.5" cy="5.5" r="3.2" fill="none" stroke="currentColor" strokeWidth="1.2" />
      )}

      {kind === 'running' && (
        <>
          <circle cx="5.5" cy="5.5" r="3.4" fill="none" stroke="currentColor" strokeWidth="1.2" opacity="0.3" />
          <g className="animate-ledger-sweep" style={{ transformOrigin: '5.5px 5.5px' }}>
            <path
              d="M5.5 2.1a3.4 3.4 0 0 1 3.4 3.4"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.6"
              strokeLinecap="round"
            />
          </g>
        </>
      )}

      {kind === 'failed' && (
        <path
          d="M3.1 3.1l4.8 4.8M7.9 3.1l-4.8 4.8"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.6"
          strokeLinecap="round"
        />
      )}

      {kind === 'expired' && (
        <circle
          cx="5.5"
          cy="5.5"
          r="3.3"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.3"
          strokeDasharray="1.7 1.5"
        />
      )}

      {kind === 'cancelled' && (
        <path d="M2.4 5.5h6.2" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
      )}

      {kind === 'skipped' && (
        <>
          <circle cx="5.5" cy="5.5" r="3.2" fill="none" stroke="currentColor" strokeWidth="1.1" opacity="0.7" />
          <path d="M3.6 7.4l3.8-3.8" fill="none" stroke="currentColor" strokeWidth="1.1" strokeLinecap="round" />
        </>
      )}
    </svg>
  );
}
